/**
 * Green Flag — semantic tokens
 *
 * These are the tokens components SHOULD reference. They map semantic intent
 * (e.g. "the primary brand surface") onto the raw primitive tokens in `tokens.ts`.
 * Swapping the underlying scale (or a future dark mode) only requires editing
 * this file — components stay untouched.
 */

import { defineSemanticTokens } from "@chakra-ui/react";

export const semanticTokens = defineSemanticTokens({
  colors: {
    // ---- Brand surfaces ----
    bg: {
      canvas:    { value: "{colors.gf.surface.white}" },
      muted:     { value: "{colors.gf.surface.muted}"  },
      subtle:    { value: "{colors.gf.surface.subtle}" }, // inactive pill bg
      brand:     { value: "{colors.gf.green.500}"     }, // hero band, primary btn
      brandSoft: { value: "{colors.gf.green.100}"     }, // active tab chip
      brandTint: { value: "rgba(57, 184, 125, 0.25)"  }, // sparks chip
      friends:   { value: "{colors.gf.blue.500}"      },
      friendsSoft: { value: "rgba(11, 128, 205, 0.12)" }, // friends icon disc / chip

      danger:    { value: "{colors.gf.red.500}"       },
      // A selected control's fill, such as a ticked checkbox: brand green dark
      // enough to reach 3:1 on white (6.1:1), which bg.brand does not.
      brandStrong: { value: "{colors.gf.green.700}" },
    },

    // ---- Foreground (text, icons) ----
    fg: {
      default:   { value: "{colors.gf.ink.900}" }, // primary ink on white
      muted:     { value: "{colors.gf.ink.600}" }, // helper / secondary
      onBrand:   { value: "{colors.gf.surface.white}" }, // text on green hero / primary btn
      onTint:    { value: "{colors.gf.green.800}"     }, // text on mint chip
      brand:     { value: "{colors.gf.green.500}"     }, // active nav, brand icon on white
      brandSoft: { value: "{colors.gf.green.200}"     }, // inactive nav glyph
      friends:   { value: "{colors.gf.blue.500}"      },
      danger:    { value: "{colors.gf.red.500}"       },
    },

    // ---- Borders ----
    border: {
      default: { value: "{colors.gf.line.strong}" },
      subtle:  { value: "{colors.gf.line.soft}"   },
      brand:   { value: "{colors.gf.green.500}"   },
      friends: { value: "{colors.gf.blue.500}"    },
      // The edge of a control a person operates (stepper, checkbox, price
      // field): 3:1 on white (3.6:1), as a control boundary must be. The
      // hairline border.default is 1.4:1 and reads as decoration.
      control: { value: "{colors.gf.gray.medium}" },
      // The edge of a control that is on or holds a value (6.1:1).
      brandStrong: { value: "{colors.gf.green.700}" },
    },

    // ---- Admin and host workspaces (AdminShell) ----
    // The workspace keeps its own chrome so staff always know which side of
    // Green Flag they are on: a dark forest top bar over the app's own white
    // sidebar (bg.canvas), mint current item (greenLight.subtle, fg.onTint)
    // and gray page (bg.subtle). Only what the app has no token for is here.
    admin: {
      chrome: {
        bg:      { value: "{colors.gf.green.900}" },       // the top bar
        fg:      { value: "{colors.gf.surface.white}" },   // text and icons on it (15.2:1)
        fgMuted: { value: "{colors.gf.green.100}" },       // inactive ModeSwitch label (8.9:1 on the track)
        hover:   { value: "rgba(255, 255, 255, 0.06)" },   // icon button hover
        track:   { value: "rgba(255, 255, 255, 0.10)" },   // ModeSwitch track
      },
      badge: {
        bg: { value: "{colors.gf.green.500}" },            // "Admin" / "Check-in" chip
        fg: { value: "{colors.gf.green.900}" },            // 6.0:1
      },
      nav: {
        activeIcon: { value: "{colors.gf.green.700}" },    // 5.5:1 on greenLight.subtle
      },
      // One solid focus ring for the workspace: 3:1 or better on every surface
      // it lands on, the dark top bar included.
      focus: { value: "{colors.gf.green.600}" },
      scrim: { value: "rgba(26, 41, 33, 0.48)" },          // behind the phone nav drawer
    },

    // ---- Compat aliases for pre-migration callers (lib/chakraTheme.ts).
    // Safe to remove once each consumer is moved to `gf.*` / semantic tokens.
    brand: {
      green: {
        lightest: { value: "{colors.gf.green.50}"  },
        light:    { value: "{colors.gf.green.500}" },
        muted:    { value: "{colors.gf.green.300}" },
        primary:  { value: "{colors.gf.green.700}" },
        dark:     { value: "{colors.gf.green.900}" },
      },
      blue: {
        light:   { value: "{colors.gf.blue.300}" },
        primary: { value: "{colors.gf.blue.500}" },
        dark:    { value: "{colors.gf.blue.900}" },
      },
      orange: {
        primary: { value: "{colors.gf.orange.primary}" },
        dark:    { value: "{colors.gf.orange.dark}"    },
      },
      red: {
        primary: { value: "{colors.gf.red.legacyPrimary}" },
        dark:    { value: "{colors.gf.red.legacyDark}"    },
      },
      neutral: {
        dark:   { value: "{colors.gf.neutral.dark}"   },
        medium: { value: "{colors.gf.neutral.medium}" },
        light:  { value: "{colors.gf.neutral.light}"  },
      },
      gray: {
        primary: { value: "{colors.gf.gray.primary}" },
        medium:  { value: "{colors.gf.gray.medium}"  },
        dark:    { value: "{colors.gf.gray.dark}"    },
      },
    },

    // ---- Chakra v3 palette-shaped aliases (colorPalette="greenLight" etc).
    greenLight: {
      solid:    { value: "{colors.gf.green.500}" },
      contrast: { value: "{colors.gf.green.900}" },
      fg:       { value: "{colors.gf.green.900}" },
      subtle:   { value: "{colors.gf.green.50}"  },
      muted:    { value: "{colors.gf.green.300}" },
    },
    greenDark: {
      solid:    { value: "{colors.gf.green.900}" },
      contrast: { value: "{colors.gf.neutral.light}" },
      fg:       { value: "{colors.gf.green.900}" },
      subtle:   { value: "{colors.gf.green.50}"  },
      muted:    { value: "{colors.gf.green.300}" },
    },
    orange: {
      solid:    { value: "{colors.gf.orange.primary}" },
      contrast: { value: "white" },
      fg:       { value: "{colors.gf.orange.dark}"    },
      subtle:   { value: "#F3CBB1" },
      muted:    { value: "{colors.gf.orange.primary}" },
    },
    brandRed: {
      solid:    { value: "{colors.gf.red.legacyPrimary}" },
      contrast: { value: "white" },
      fg:       { value: "{colors.gf.red.legacyDark}"    },
      subtle:   { value: "{colors.gf.red.100}"           },
      muted:    { value: "{colors.gf.red.legacyPrimary}" },
    },
  },
});
